import { fetchJson } from "./api.js";
import { truncateLabel } from "../../../../utils/textPresentation.js";
import { fetchAmortismanDefinitions } from "../../../amortisman/amortismanUtils.js";

let kisilerByTc = new Map();

const PRODUCT_TREE_CONFIG = [
	{
		key: "departman",
		selector: "departman",
		placeholder: "Departman se\u00e7iniz",
	},
	{
		key: "departman_urun_grubu",
		selector: "urun_grubu",
		placeholder: "\u00dcr\u00fcn grubu se\u00e7iniz",
		path: (state) => `/urun-agaci/departman-gruplari?departman_id=${state.departman_id}`,
	},
	{
		key: "urun_grubu_urun_alt_grubu",
		selector: "urun_alt_grubu",
		placeholder: "\u00dcr\u00fcn alt grubu se\u00e7iniz",
		path: (state) => `/urun-agaci/urun-alt-gruplari?departman_urun_grubu_id=${state.departman_urun_grubu_id}`,
	},
	{
		key: "urun_alt_grubu_urun_cinsi",
		selector: "urun_cinsi",
		placeholder: "\u00dcr\u00fcn cinsi se\u00e7iniz",
		path: (state) => `/urun-agaci/urun-cinsleri?urun_grubu_urun_alt_grubu_id=${state.urun_grubu_urun_alt_grubu_id}`,
	},
	{
		key: "urun_cinsi_marka",
		selector: "marka",
		placeholder: "Marka se\u00e7iniz",
		path: (state) => `/urun-agaci/markalar?urun_alt_grubu_urun_cinsi_id=${state.urun_alt_grubu_urun_cinsi_id}`,
	},
	{
		key: "marka_model",
		selector: "model",
		placeholder: "Model se\u00e7iniz",
		path: (state) => `/urun-agaci/modeller?urun_cinsi_marka_id=${state.urun_cinsi_marka_id}`,
	},
];

function getDialogPrefix(dialog) {
	return dialog.querySelector("#create_departman") ? "create" : "edit";
}

function getProductTreeElements(dialog) {
	const prefix = getDialogPrefix(dialog);
	return {
		prefix,
		departman: dialog.querySelector(`#${prefix}_departman`),
		urun_grubu: dialog.querySelector(`#${prefix}_urun_grubu`),
		urun_alt_grubu: dialog.querySelector(`#${prefix}_urun_alt_grubu`),
		urun_cinsi: dialog.querySelector(`#${prefix}_urun_cinsi`),
		marka: dialog.querySelector(`#${prefix}_marka`),
		model: dialog.querySelector(`#${prefix}_model`),
	};
}

function getSelectContainer(selectEl) {
	return selectEl?.closest(".column-container") || null;
}

function setSelectLoading(selectEl, isLoading) {
	const container = getSelectContainer(selectEl);
	if (!container) return;
	container.classList.toggle("loading", Boolean(isLoading));
}

function fillSelect(selectEl, placeholderText, items, options = {}) {
	if (!selectEl) return;

	const selectedValue = options.selectedValue == null ? "" : String(options.selectedValue);
	selectEl.innerHTML = "";

	const ph = document.createElement("option");
	ph.value = "";
	ph.textContent = placeholderText;
	ph.title = placeholderText;
	ph.selected = !selectedValue;
	selectEl.appendChild(ph);

	(items || []).forEach((item) => {
		const fullLabel = String(item.name ?? "");
		const opt = document.createElement("option");
		opt.value = String(item.id);
		opt.textContent = truncateLabel(fullLabel, 32);
		opt.title = fullLabel;
		selectEl.appendChild(opt);
	});

	const hasSelectedValue = (items || []).some((item) => String(item.id) === selectedValue);
	selectEl.value = hasSelectedValue ? selectedValue : "";
	selectEl.disabled = Boolean(options.disabled);
	const selected = selectEl.options?.[selectEl.selectedIndex];
	selectEl.title = selected?.title?.trim?.() || selected?.textContent?.trim() || "";
}

function resetTreeSelect(selectEl, placeholderText, disabled = true) {
	setSelectLoading(selectEl, false);
	fillSelect(selectEl, placeholderText, [], { disabled, selectedValue: "" });
}

async function loadKisilerForCreateDialog(dialog) {
	const select = dialog.querySelector("#create_zimmetlenen_kisi");
	if (!select) return;

	fillSelect(select, "Zimmetlenen ki\u015fi se\u00e7iniz", [], { disabled: false });

	const data = await fetchJson("/kisiler?durum=1");
	kisilerByTc = new Map();

	(data.items || []).forEach((k) => {
		const tc = String(k.tc);
		kisilerByTc.set(tc, {
			tc,
			unvan: k.unvan || "",
			departman_adi: k.departman_adi || "",
		});
	});

	fillSelect(
		select,
		"Zimmetlenen ki\u015fi se\u00e7iniz",
		(data.items || []).map((item) => ({
			id: String(item.tc),
			name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
		})),
		{ disabled: false, selectedValue: "" },
	);
}

function bindZimmetInfoCard(dialog) {
	const select = dialog.querySelector("#create_zimmetlenen_kisi");
	const card = dialog.querySelector("#zimmetInfoCard");
	if (!select || !card || select.dataset.zimmetBound === "true") return;
	select.dataset.zimmetBound = "true";

	const t1 = card.querySelector(".zimmet-tc-deger");
	const t2 = card.querySelector(".zimmet-unvan-deger");
	const t3 = card.querySelector(".zimmet-departman-deger");

	const setEmpty = () => {
		if (t1) t1.textContent = "\u2014";
		if (t2) t2.textContent = "\u2014";
		if (t3) t3.textContent = "\u2014";
	};

	const renderSelected = () => {
		const tc = select.value;
		if (!tc) return setEmpty();
		const kisi = kisilerByTc.get(tc);
		if (!kisi) return setEmpty();
		if (t1) t1.textContent = kisi.tc;
		if (t2) t2.textContent = kisi.unvan || "\u2014";
		if (t3) t3.textContent = kisi.departman_adi || "\u2014";
	};

	setEmpty();
	select.addEventListener("change", renderSelected);
	renderSelected();
}

async function fillProductTreeLevel(dialog, levelIndex, selectedValue = "") {
	const elements = getProductTreeElements(dialog);
	const config = PRODUCT_TREE_CONFIG[levelIndex];
	const selectEl = elements[config.selector];
	if (!selectEl) return [];

	if (levelIndex === 0) return [];

	const state = {
		departman_id: elements.departman?.value || "",
		departman_urun_grubu_id: elements.urun_grubu?.value || "",
		urun_grubu_urun_alt_grubu_id: elements.urun_alt_grubu?.value || "",
		urun_alt_grubu_urun_cinsi_id: elements.urun_cinsi?.value || "",
		urun_cinsi_marka_id: elements.marka?.value || "",
	};

	const requiredStateKey = Object.keys(state)[levelIndex - 1];
	if (!state[requiredStateKey]) {
		resetTreeSelect(selectEl, config.placeholder, true);
		return [];
	}

	setSelectLoading(selectEl, true);
	try {
		const data = await fetchJson(config.path(state));
		const items = data.items || [];
		fillSelect(selectEl, config.placeholder, items, { disabled: false, selectedValue });
		return items;
	} finally {
		setSelectLoading(selectEl, false);
	}
}

function resetLowerProductTreeLevels(dialog, startIndex) {
	const elements = getProductTreeElements(dialog);
	for (let i = startIndex; i < PRODUCT_TREE_CONFIG.length; i += 1) {
		const config = PRODUCT_TREE_CONFIG[i];
		resetTreeSelect(elements[config.selector], config.placeholder, i !== 0);
	}
}

function bindProductTreeCascade(dialog) {
	if (dialog.dataset.productTreeBound === "true") return;
	dialog.dataset.productTreeBound = "true";

	const elements = getProductTreeElements(dialog);
	const chain = [
		elements.departman,
		elements.urun_grubu,
		elements.urun_alt_grubu,
		elements.urun_cinsi,
		elements.marka,
	];

	chain.forEach((selectEl, index) => {
		if (!selectEl) return;
		selectEl.addEventListener("change", async () => {
			resetLowerProductTreeLevels(dialog, index + 1);
			if (!selectEl.value) return;
			await fillProductTreeLevel(dialog, index + 1);
		});
	});
}

async function initProductTreeDropdowns(dialog, departmanlar, selection = null) {
	const elements = getProductTreeElements(dialog);
	if (!elements.departman) return;

	bindProductTreeCascade(dialog);
	fillSelect(elements.departman, PRODUCT_TREE_CONFIG[0].placeholder, departmanlar, {
		disabled: false,
		selectedValue: selection?.departman_id,
	});
	resetLowerProductTreeLevels(dialog, 1);

	if (!selection?.departman_id) return;

	await fillProductTreeLevel(dialog, 1, selection.departman_urun_grubu_id);
	if (!selection?.departman_urun_grubu_id) return;

	await fillProductTreeLevel(dialog, 2, selection.urun_grubu_urun_alt_grubu_id);
	if (!selection?.urun_grubu_urun_alt_grubu_id) return;

	await fillProductTreeLevel(dialog, 3, selection.urun_alt_grubu_urun_cinsi_id);
	if (!selection?.urun_alt_grubu_urun_cinsi_id) return;

	await fillProductTreeLevel(dialog, 4, selection.urun_cinsi_marka_id);
	if (!selection?.urun_cinsi_marka_id) return;

	await fillProductTreeLevel(dialog, 5, selection.marka_model_id);
}

export async function initCreateDialogDropdowns(dialogEl, options = {}) {
	const dialog = dialogEl || document.querySelector("#createDialog");
	if (!dialog) return null;

	const prefix = getDialogPrefix(dialog);

	try {
		const [
			departmanlar,
			urunDurumlari,
			zimmetDurumlari,
			lokasyonlar,
			odalar,
			yerler,
			amortismanTanimlari,
		] = await Promise.all([
			fetchJson("/departmanlar").then((d) => d.items || []),
			fetchJson("/urun-durumlari").then((d) => d.items || []),
			fetchJson("/zimmet-durumlari").then((d) => d.items || []),
			fetchJson("/lokasyonlar").then((d) => d.items || []),
			fetchJson("/odalar").then((d) => d.items || []),
			fetchJson("/yerler").then((d) => d.items || []),
			fetchAmortismanDefinitions(),
		]);

		await initProductTreeDropdowns(dialog, departmanlar, options.productTreeSelection || null);

		fillSelect(dialog.querySelector(`#${prefix}_urun_durumu`), "\u00dcr\u00fcn durumu se\u00e7iniz", urunDurumlari, { disabled: false });
		fillSelect(dialog.querySelector(`#${prefix}_zimmet_durumu`), "Zimmet durumu se\u00e7iniz", zimmetDurumlari, { disabled: false });
		fillSelect(dialog.querySelector(`#${prefix}_lokasyon`), "Lokasyon se\u00e7iniz", lokasyonlar, { disabled: false });
		fillSelect(dialog.querySelector(`#${prefix}_oda`), "Oda se\u00e7iniz", odalar, { disabled: false });
		fillSelect(dialog.querySelector(`#${prefix}_yer`), "Yer se\u00e7iniz", yerler, { disabled: false });

		if (prefix === "create") {
			await loadKisilerForCreateDialog(dialog);
			bindZimmetInfoCard(dialog);
		}

		return {
			departmanlar,
			urunDurumlari,
			zimmetDurumlari,
			lokasyonlar,
			odalar,
			yerler,
			amortismanTanimlari,
		};
	} catch (err) {
		console.error("Dropdown init hatasi:", err);
		return null;
	}
}

export function resetCreateZimmetInfoCard(dialogEl) {
	const dialog = dialogEl || document.querySelector("#createDialog");
	const card = dialog?.querySelector("#zimmetInfoCard");
	if (!card) return;

	const tcEl = card.querySelector(".zimmet-tc-deger");
	const unvanEl = card.querySelector(".zimmet-unvan-deger");
	const departmanEl = card.querySelector(".zimmet-departman-deger");

	if (tcEl) tcEl.textContent = "\u2014";
	if (unvanEl) unvanEl.textContent = "\u2014";
	if (departmanEl) departmanEl.textContent = "\u2014";
}
